import type { FC, ReactNode } from 'react'

import { useRouter } from 'next/router'
import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react'
import { cn } from '@/lib/utils'
import { gsap, useGSAP, prefersReducedMotion } from '@/lib/gsap'
import { onStageReady } from '@/lib/stage'
import { useLenis } from '@/components/motion/smooth-scroll'
import { Logo } from '@/components/generals/logo'
import { Button } from '@/components/generals/button'
import { Container } from '@/components/generals/container'
import { RollText } from '@/components/generals/roll-text'
import { MobileMenu } from '@/components/generals/mobile-menu'
import { TransitionLink } from '@/components/generals/transition-link'

import { NAV_LINKS, PRIMARY_CTA } from '@/constants/site'

export const Header: FC = (): ReactNode => {
    const router = useRouter()
    const lenis = useLenis()
    const headerRef = useRef<HTMLElement>(null)
    const toggleRef = useRef<HTMLButtonElement>(null)
    const [scrolled, setScrolled] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    const activePath = router.pathname

    const closeMenu = useCallback(() => {
        setMenuOpen(false)
    }, [])

    // Entrance once the intro screen lifts
    useGSAP((_context, contextSafe) => {
        if (!contextSafe || prefersReducedMotion()) return

        const items = headerRef.current?.querySelectorAll('[data-header-item]')

        if (!items?.length) return

        gsap.set(items, { autoAlpha: 0, yPercent: -60 })

        return onStageReady(contextSafe(() => {
            gsap.to(items, {
                autoAlpha: 1,
                yPercent: 0,
                duration: 1.1,
                stagger: 0.06,
                delay: 0.35,
                ease: 'expo.out',
                clearProps: 'transform'
            })
        }))
    }, { scope: headerRef })

    // Glass background after a little scroll, hide while scrolling down
    useEffect(() => {
        let lastY = window.scrollY
        let frame = 0

        const update = (): void => {
            frame = 0

            const y = window.scrollY

            setScrolled(y > 24)

            if (y > 360 && y > lastY + 4) setHidden(true)
            else if (y < lastY - 4 || y <= 360) setHidden(false)

            lastY = y
        }

        const onScroll = (): void => {
            if (!frame) frame = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => {
            window.removeEventListener('scroll', onScroll)
            cancelAnimationFrame(frame)
        }
    }, [])

    useEffect(() => {
        const reset = (): void => {
            setHidden(false)
            setMenuOpen(false)
        }

        router.events.on('routeChangeComplete', reset)

        return () => {
            router.events.off('routeChangeComplete', reset)
        }
    }, [router])

    // Lock scrolling and wire up Escape while the mobile menu is open
    useEffect(() => {
        if (!menuOpen) return

        lenis?.stop()
        document.documentElement.style.overflow = 'hidden'

        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setMenuOpen(false)
        }

        window.addEventListener('keydown', onKey)

        return () => {
            lenis?.start()
            document.documentElement.style.overflow = ''
            window.removeEventListener('keydown', onKey)
            toggleRef.current?.focus({ preventScroll: true })
        }
    }, [menuOpen, lenis])

    return (
        <>
            <header
                ref={headerRef}
                className={cn(
                    'fixed inset-x-0 top-0 z-50 transition-transform duration-700 ease-expo',
                    hidden && !menuOpen ? '-translate-y-full' : 'translate-y-0'
                )}
            >
                <div
                    aria-hidden='true'
                    className={cn(
                        'absolute inset-0 border-b transition-[background-color,border-color,backdrop-filter] duration-500',
                        scrolled && !menuOpen
                            ? 'border-line bg-background/75 backdrop-blur-xl'
                            : 'border-transparent bg-transparent'
                    )}
                />

                <Container className='relative flex h-[72px] items-center justify-between gap-6'>
                    <TransitionLink
                        href='/'
                        aria-label='Cortex home'
                        data-header-item
                        onClick={closeMenu}
                        className='relative z-[2]'
                    >
                        <Logo />
                    </TransitionLink>

                    <nav aria-label='Main' className='hidden lg:block'>
                        <ul className='flex items-center gap-1'>
                            {NAV_LINKS.map((link) => {
                                const active = activePath === link.href || activePath.startsWith(`${link.href}/`)

                                return (
                                    <li key={link.href} data-header-item>
                                        <TransitionLink
                                            href={link.href}
                                            aria-current={active ? 'page' : undefined}
                                            className={cn(
                                                'group relative flex items-center px-3.5 py-2 text-[0.9375rem] tracking-[-0.01em] transition-colors duration-300',
                                                active ? 'text-foreground' : 'text-soft hover:text-foreground'
                                            )}
                                        >
                                            <RollText>{link.label}</RollText>

                                            <span
                                                aria-hidden='true'
                                                className={cn(
                                                    'absolute bottom-0 left-1/2 size-1 -translate-x-1/2 bg-ember transition-all duration-500 ease-expo',
                                                    active ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
                                                )}
                                            />
                                        </TransitionLink>
                                    </li>
                                )
                            })}
                        </ul>
                    </nav>

                    <div className='relative z-[2] flex items-center gap-3'>
                        <div data-header-item className='hidden sm:block'>
                            <Button href={PRIMARY_CTA.href}>
                                {PRIMARY_CTA.label}
                            </Button>
                        </div>

                        <button
                            ref={toggleRef}
                            type='button'
                            data-header-item
                            aria-expanded={menuOpen}
                            aria-controls='mobile-menu'
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                            onClick={() => setMenuOpen((value) => !value)}
                            className='relative flex size-12 items-center justify-center border border-line-strong bg-white/[0.03] backdrop-blur-md transition-colors hover:bg-white/[0.08] lg:hidden'
                        >
                            <span
                                className={cn(
                                    'absolute h-px w-5 bg-foreground transition-transform duration-500 ease-expo',
                                    menuOpen ? 'rotate-45' : '-translate-y-[4px]'
                                )}
                            />

                            <span
                                className={cn(
                                    'absolute h-px w-5 bg-foreground transition-transform duration-500 ease-expo',
                                    menuOpen ? '-rotate-45' : 'translate-y-[4px]'
                                )}
                            />
                        </button>
                    </div>
                </Container>
            </header>

            <MobileMenu
                open={menuOpen}
                activePath={activePath}
                onClose={closeMenu}
            />
        </>
    )
}
